import { useEffect, useState } from 'react';
import { USER_ROLES, type CreateInviteResponse, type TeamResponse } from '@stonelink/shared';
import { api } from '../api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  formatDate,
} from '../ui';

const ROLE_HELP: Record<(typeof USER_ROLES)[number], string> = {
  owner: 'Everything, including settings, the install tag and the backend connection.',
  staff: 'Train the assistant, edit the notebook, answer gaps and read conversations.',
};

/** Who else can use this workspace. Owner-only. */
export function TeamPage() {
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<(typeof USER_ROLES)[number]>('staff');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Shown once, right after creation — the API will not return it again. */
  const [freshInvite, setFreshInvite] = useState<CreateInviteResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => setTeam(await api.getTeam());

  useEffect(() => {
    void load().catch(() => setError('Could not load your team.'));
  }, []);

  const run = async (work: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : fallback);
    } finally {
      setBusy(false);
    }
  };

  const invite = () =>
    run(async () => {
      setFreshInvite(await api.createInvite({ email, role }));
      setEmail('');
      await load();
    }, 'Could not create that invitation.');

  if (!team) {
    return (
      <div>
        <PageHeader title="Team" description="Who else can use this workspace." />
        {error ? <ErrorNote>{error}</ErrorNote> : <p className="text-sm text-muted">Loading…</p>}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <PageHeader
        title="Team"
        description="Invite colleagues to train the assistant with you. Only owners can change settings, the install tag or the backend connection."
      />

      {error && <ErrorNote>{error}</ErrorNote>}

      <Card title="Invite someone" description="You send the link yourself — we do not email it.">
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
            <div>
              <Label htmlFor="invite-email">Their email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="invite-role">Role</Label>
              <Select
                id="invite-role"
                value={role}
                onChange={(event) => setRole(event.target.value as (typeof USER_ROLES)[number])}
              >
                {USER_ROLES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <p className="text-sm text-muted">{ROLE_HELP[role]}</p>

          <Button disabled={busy || !email.trim()} onClick={() => void invite()}>
            Create invitation
          </Button>

          {freshInvite && (
            <div className="space-y-3 rounded-xl border border-amber-300/30 bg-amber-300/10 p-4">
              <p className="text-sm text-amber-200">
                Copy this link and send it to {freshInvite.invite.email}. It works once, expires{' '}
                {formatDate(freshInvite.invite.expiresAt)}, and is not shown again.
              </p>
              <code className="block break-all rounded-xl border border-line bg-white/5 px-3 py-2 font-mono text-xs">
                {freshInvite.acceptUrl}
              </code>
              <Button
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard.writeText(freshInvite.acceptUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card title="Members">
        <ul className="space-y-2">
          {team.members.map((member) => (
            <li
              key={member.id}
              className="glass-flat flex flex-wrap items-center justify-between gap-3 p-4"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{member.email}</p>
                <p className="mt-1 text-xs text-muted">Joined {formatDate(member.createdAt)}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge tone={member.role === 'owner' ? 'brand' : 'neutral'}>{member.role}</Badge>
                {member.role !== 'owner' && (
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await api.removeMember(member.id);
                        await load();
                      }, 'Could not remove them.')
                    }
                  >
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Pending invitations">
        {team.invites.length === 0 ? (
          <EmptyState title="No invitations outstanding" />
        ) : (
          <ul className="space-y-2">
            {team.invites.map((pending) => (
              <li
                key={pending.id}
                className="glass-flat flex flex-wrap items-center justify-between gap-3 p-4"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{pending.email}</p>
                  <p className="mt-1 text-xs text-muted">Expires {formatDate(pending.expiresAt)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge tone="warn">{pending.role}</Badge>
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await api.revokeInvite(pending.id);
                        await load();
                      }, 'Could not revoke that invitation.')
                    }
                  >
                    Revoke
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
